import { SettlementDetail } from '../../../models/Entities/SettlementDetails/SettlementDetail';
import { SettlementStatusEnum } from '../../../models/Enums/SettlementStatusEnum';
import { ProtocolTitleLookup, protocolLabel } from '../SettlementProtocolBreakdown/SettlementProtocolBreakdown.Logic';

/**
 * Detalle de una liquidación de proveedor agrupado por protocolo (spec 010 US4, data-model §3.5), sin React ni
 * store. Cada línea es un consumo del proveedor: servicio = `description`, cantidad = `quantityToSettle`.
 */

export interface ProviderSettlementLine {
    id: number;
    /** `patientNumberInProtocol` o, si falta, `patientRecordNumber`. */
    patient: string;
    /** `dueDate`: fecha del consumo. */
    date: Date | undefined;
    /** `description` (contrato §5); `conceptName` de respaldo. */
    service: string;
    /** `quantityToSettle` (contrato §5); 1 si falta. */
    quantity: number;
    unitAmount: number;
    amount: number;
    currency: string;
}

export interface CurrencyTotal {
    currency: string;
    amount: number;
}

export interface ProviderSettlementGroup {
    /** `p-{protocolId}` o `none` ("Sin protocolo"). */
    key: string;
    protocolId: number | null;
    label: string;
    lines: ProviderSettlementLine[];
    totals: CurrencyTotal[];
}

export const NO_PROTOCOL_KEY = 'none';

const toDate = (value: Date | string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

const lineOf = (detail: SettlementDetail): ProviderSettlementLine => {
    const quantity = Number(detail.quantityToSettle);
    const patient = (detail.patientNumberInProtocol ?? '').toString().trim()
        || (detail.patientRecordNumber ? String(detail.patientRecordNumber) : '');
    return {
        id: detail.id,
        patient,
        date: toDate(detail.dueDate as Date | string | undefined),
        service: (detail.description ?? '').trim() || (detail.conceptName ?? '').trim(),
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        unitAmount: Number(detail.unitAmount) || 0,
        amount: Number(detail.amount) || 0,
        currency: (detail.currency ?? '').trim().toUpperCase(),
    };
};

/** Total por moneda de las líneas, ordenado por código de moneda. */
export const totalsByCurrency = (lines: ProviderSettlementLine[]): CurrencyTotal[] => {
    const totals = new Map<string, number>();
    lines.forEach((line) => totals.set(line.currency, (totals.get(line.currency) ?? 0) + line.amount));
    return Array.from(totals.entries())
        .map(([currency, amount]) => ({ currency, amount }))
        .sort((a, b) => a.currency.localeCompare(b.currency));
};

const compareLabels = (a: string, b: string): number => a.localeCompare(b, 'es', { sensitivity: 'base', numeric: true });

/**
 * Agrupa los detalles por `protocolId`. Etiqueta del grupo: título del protocolo del store, si no `protocolName`
 * (número), si no "Protocolo #id"; los detalles sin protocolo van a "Sin protocolo" (`noProtocolLabel`, ya
 * traducido). Orden: por etiqueta (numérico, sin acentos) y "Sin protocolo" siempre al final. Las líneas de cada
 * grupo, por fecha y después por id.
 */
export const groupDetailsByProtocol = (
    details: SettlementDetail[] | null | undefined,
    titleOf?: ProtocolTitleLookup,
    noProtocolLabel: string = 'Sin protocolo'
): ProviderSettlementGroup[] => {
    const groups = new Map<string, { protocolId: number | null; protocolName?: string | null; lines: ProviderSettlementLine[] }>();
    (details ?? []).filter(Boolean).forEach((detail) => {
        const protocolId = detail.protocolId ? Number(detail.protocolId) : null;
        const key = protocolId ? `p-${protocolId}` : NO_PROTOCOL_KEY;
        const group = groups.get(key) ?? { protocolId, protocolName: detail.protocolName, lines: [] };
        if (!group.protocolName && detail.protocolName) group.protocolName = detail.protocolName;
        group.lines.push(lineOf(detail));
        groups.set(key, group);
    });

    const result: ProviderSettlementGroup[] = Array.from(groups.entries()).map(([key, group]) => ({
        key,
        protocolId: group.protocolId,
        label: group.protocolId ? protocolLabel({ protocolId: group.protocolId, protocolName: group.protocolName }, titleOf) : noProtocolLabel,
        lines: [...group.lines].sort((a, b) => ((a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0)) || (a.id - b.id)),
        totals: totalsByCurrency(group.lines),
    }));

    return result.sort((a, b) => {
        if (a.key === NO_PROTOCOL_KEY) return b.key === NO_PROTOCOL_KEY ? 0 : 1;
        if (b.key === NO_PROTOCOL_KEY) return -1;
        return compareLabels(a.label, b.label) || ((a.protocolId ?? 0) - (b.protocolId ?? 0));
    });
};

/** Total por moneda de toda la liquidación (todas las líneas de todos los grupos). */
export const settlementTotals = (groups: ProviderSettlementGroup[]): CurrencyTotal[] =>
    totalsByCurrency(groups.flatMap((group) => group.lines));

/** Registrada (1) o En curso (2): la generación asincrónica todavía no terminó. */
export const isSettlementCalculating = (status: number | null | undefined): boolean =>
    status === SettlementStatusEnum.REGISTERED || status === SettlementStatusEnum.IN_PROGRESS;

/** Con estado Falló (7): el `comments` de la liquidación, o `undefined` para mostrar el texto genérico. */
export const settlementFailureMessage = (
    settlement: { status?: number | null; comments?: string | null } | null | undefined
): string | undefined => {
    if (!settlement || settlement.status !== SettlementStatusEnum.FAIL) return undefined;
    const comments = (settlement.comments ?? '').trim();
    return comments || undefined;
};
